

import { useSelector } from "react-redux";
import {
  DASHBOARD,
  LOGIN,
  VENDOR_DASHBOARD,
} from "./component/common/Routes/AppRoutes";
import { CUSTOMER_DASHBOARD } from "./component/common/Routes/CustomerRoutes";
import { useCallback, useEffect, useState } from "react";
import Logo from "./assets/AtithisevaFavIcon.jpg";
import { BrowserRouter } from "react-router-dom";
import { AppContent } from "./AppContent";
import { NEW_BOOKING_PAGES } from "./component/common/Routes/NewBookingPages";

const App = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [anchorElUser, setAnchorElUser] = useState(null);
  const [faviconUrl, setFaviconUrl] = useState(Logo);
  const user = useSelector((state) => state.auth.user);

  useEffect(() => {
    const link =
      document.querySelector("link[rel*='icon']") ||
      document.createElement("link");
    link.rel = "icon";
    link.href = faviconUrl;
    document.getElementsByTagName("head")[0].appendChild(link);
  }, [faviconUrl]);

  // Function to check if user has valid tokens
 const hasValidTokens = useCallback(() => {
  // Check sessionStorage first
  const accessToken = sessionStorage.getItem("accessToken");
  const refreshToken = sessionStorage.getItem("refreshToken");
  const instantToken = sessionStorage.getItem("instantToken");
  const newBookingToken = sessionStorage.getItem("newBookingAccessToken");

  const isOnNewBookingPage = NEW_BOOKING_PAGES.includes(window.location.pathname);

  // Handle new booking flow
  if (newBookingToken && isOnNewBookingPage) {
    return false; // Not authenticated with regular tokens
  }

  return !!(accessToken || refreshToken || instantToken);
}, []);

  // Initialize authentication state
  useEffect(() => {
    const checkAuthState = () => {
      // Force a check and cleanup if needed
      const newBookingToken = localStorage.getItem("newBookingAccessToken");
      if (newBookingToken) {
        // If new booking token exists, ensure other tokens are cleared
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("instantToken");
        setIsAuthenticated(false);
        setIsLoading(false);
        return;
      }

      const isAuth = hasValidTokens();
      setIsAuthenticated(isAuth);
      setIsLoading(false);
    };

    checkAuthState();
  }, [hasValidTokens]);

  
  // Listen for auth state changes from other tabs
  useEffect(() => {
    const handleAuthStateChange = (event) => {
      const { type } = event.detail;

      if (type === "LOGOUT" || type === "TOKEN_REMOVED") {
       
        setIsAuthenticated(false);
        setIsDrawerOpen(false);
      }
    };

    window.addEventListener("authStateChange", handleAuthStateChange);

    return () => {
      window.removeEventListener("authStateChange", handleAuthStateChange);
    };
  }, []);

  // Update authentication state when user changes
  useEffect(() => {
    const isAuth = hasValidTokens();

    if (!isAuth) {
      setIsDrawerOpen(false);
      setIsAuthenticated(false);
    } else {
      setIsAuthenticated(true);
    }
  }, [user, hasValidTokens]);

  // Listen for storage changes (for cross-tab synchronization)
  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === "newBookingAccessToken") {
        if (event.newValue) {
          // New booking token added, clear auth state and other tokens
          localStorage.removeItem("accessToken");
          localStorage.removeItem("refreshToken");
          localStorage.removeItem("instantToken");
          setIsAuthenticated(false);
          setIsDrawerOpen(false);
        } else {
          // New booking token removed, check if we should restore auth
          const isAuth = hasValidTokens();
          setIsAuthenticated(isAuth);
        }
      } else if (
        (event.key === "accessToken" ||
          event.key === "refreshToken" ||
          event.key === "instantToken") &&
        !event.newValue
      ) {
        // Auth tokens removed, update state only if no new booking token
        const newBookingToken = localStorage.getItem("newBookingAccessToken");
        if (!newBookingToken) {
          setIsAuthenticated(false);
          setIsDrawerOpen(false);
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [hasValidTokens]);

  const handleDrawerToggle = () => setIsDrawerOpen(!isDrawerOpen);
  const handleOpenUserMenu = (event) => setAnchorElUser(event.currentTarget);
  const handleCloseUserMenu = () => setAnchorElUser(null);

  const getDashboardRoute = () => {
    // If we're still loading or no tokens exist, go to login
    if (!isAuthenticated) {
      return LOGIN;
    }

    // If tokens exist but user data isn't loaded yet, wait
    if (!user && isAuthenticated) {
      return LOGIN;
    }

    // Route based on user role
    switch (user?.role) {
      case "Admin":
      case "Staff":
        return DASHBOARD;
      case "Customer":
        return CUSTOMER_DASHBOARD;
      case "Vendor":
        return VENDOR_DASHBOARD;
      default:
        return LOGIN;
    }
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <BrowserRouter>
      <AppContent
        isAuthenticated={isAuthenticated}
        user={user}
        isDrawerOpen={isDrawerOpen}
        handleDrawerToggle={handleDrawerToggle}
        anchorElUser={anchorElUser}
        handleOpenUserMenu={handleOpenUserMenu}
        handleCloseUserMenu={handleCloseUserMenu}
        getDashboardRoute={getDashboardRoute}
      />
    </BrowserRouter>
  );
};
export default App;
